import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronLeftIcon, ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { FlexGap, IconButton, Subheading, tokens } from '@treams/treams-ui';

import { calendarEventDayKey } from '../../types/CalendarEvent';
import LearningCalendarWeek from './LearningCalendarWeek';
import LearningCalendarWeekdayHeader from './LearningCalendarWeekdayHeader';

const weeksInMonth = (year: number, month: number): number[] => {
	const firstDayOfMonth = moment(`${year}-${month}`, 'YYYY-MM-DD');
	const daysInMonth = firstDayOfMonth.daysInMonth();
	const weeks = new Set<number>();

	for (let i = 0; i < daysInMonth; i += 1) {
		const currentDay = moment(firstDayOfMonth, 'YYYY-MM-DD').add(i, 'days');

		// Only add weeks where work days are present.
		if (currentDay.isoWeekday() !== 6 && currentDay.isoWeekday() !== 7) {
			weeks.add(currentDay.isoWeek());
		}
	}

	return Array.from(weeks);
};

const LearningCalendarElement = styled.div`
	background-color: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.md}px;
	overflow: hidden;
`;

const AttendanceIndicator = styled.div<{ filled?: boolean }>`
	width: 30px;
	height: 20px;
	border: 1px solid ${tokens.border.default};
	border-radius: ${tokens.radius.xs}px;
	margin-top: -2px;
	background-color: ${(props) => (props.filled ? tokens.icon.primary : tokens.surface.white)};
`;

const CalendarLegend = styled(Flex)`
	${AttendanceIndicator}, span {
		margin-left: ${tokens.gap.element}px;
		color: ${tokens.text.secondary};
		font-size: ${tokens.typography.sizes.small}px;
	}
`;

const LearningCalendar = ({
	events,
	year,
	month,
	onNext,
	onPrev,
}: {
	events: graphql.LearningEvent[];
	year: number;
	month: number;
	onNext: () => void;
	onPrev: () => void;
}) => {
	const { t } = useTranslation();

	const monthMoment = moment(`${year}-${month}`, 'YYYY-MM');

	const eventsByDateString: Record<string, graphql.LearningEvent[]> = events.reduce(
		(result, event) => {
			const key = calendarEventDayKey(event.startDate);

			return {
				...result,
				[key]: result[key]?.concat([event]) ?? [event],
			};
		},
		{} as Record<string, graphql.LearningEvent[]>,
	);

	return (
		<>
			<LearningCalendarElement>
				<Flex mx={3} my="12px" justifyContent="space-between" alignItems="center">
					<Subheading mb={0}>
						<strong>{monthMoment.format('MMMM')}</strong>
						&nbsp;
						{monthMoment.format('YYYY')}
					</Subheading>

					<FlexGap gap={tokens.gap.inline}>
						<IconButton
							type="secondary"
							icon={<ChevronLeftIcon />}
							aria-label={monthMoment.clone().subtract(1, 'month').format('MMMM YYYY')}
							onClick={onPrev}
						/>
						<IconButton
							type="secondary"
							icon={<ChevronRightIcon />}
							aria-label={monthMoment.clone().add(1, 'month').format('MMMM YYYY')}
							onClick={onNext}
						/>
					</FlexGap>
				</Flex>

				<LearningCalendarWeekdayHeader />

				{weeksInMonth(year, month).map((week) => (
					<LearningCalendarWeek
						eventsByDateString={eventsByDateString}
						year={year}
						month={month}
						week={week}
						key={`LearningCalendarWeek-${week}`}
					/>
				))}
			</LearningCalendarElement>

			<Box mt={3}>
				<CalendarLegend justifyContent="flex-end" alignItems="center">
					<AttendanceIndicator />
					<span>{t('learning.calendar.legend.notAttending')}</span>
					<AttendanceIndicator filled />
					<span>{t('learning.calendar.legend.attending')}</span>
				</CalendarLegend>
			</Box>
		</>
	);
};

export default LearningCalendar;
